import React,{useContext} from 'react';
import { styled } from '@mui/material/styles';
import {useTranslation} from 'react-i18next';
import PropTypes from 'prop-types';
import {
  Typography,
  Drawer,
  Tooltip,
} from '@mui/material';
import TreeView from '@mui/lab/TreeView';
import TreeItem from '@mui/lab/TreeItem';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import {alpha} from '@mui/material/styles';
import Snackbar from '../../CommonComponents/Snackbar';

import {UserInfoContext} from '../../../StateObjects/UserInfoContext';
import {ProjectInfoContext} from '../../../StateObjects/ProjectInfoContext';
import {Auth} from '../../../Api/auth';

import { useMsal } from "@azure/msal-react";
const PREFIX = 'ProjectsDrawer';

const classes = {
  drawer: `${PREFIX}-drawer`,
  drawerPaper: `${PREFIX}-drawerPaper`,
  drawerHeader: `${PREFIX}-drawerHeader`,
  drawerSection: `${PREFIX}-drawerSection`,
  drawerSectionIndex: `${PREFIX}-drawerSectionIndex`,
  closeBtn: `${PREFIX}-closeBtn`,
  listIcon: `${PREFIX}-listIcon`,
  root: `${PREFIX}-root`,
  content: `${PREFIX}-content`,
  group: `${PREFIX}-group`,
  expanded: `${PREFIX}-expanded`,
  selected: `${PREFIX}-selected`,
  label: `${PREFIX}-label`,
  labelRoot: `${PREFIX}-labelRoot`,
  labelIcon: `${PREFIX}-labelIcon`,
  labelText: `${PREFIX}-labelText`
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')((
  {
    theme
  }
) => ({
  [`& .${classes.drawer}`]: {
    boxSizing: 'border-box',
    width: DRAWER_WIDTH,
    flexShrink: 0,
    zIndex: '10 !important',
  },

  [`& .${classes.drawerPaper}`]: {
    boxSizing: 'border-box',
    width: DRAWER_WIDTH,
    top: 'auto',
    paddingBottom: theme.spacing(2),
    paddingRight: theme.spacing(1.25),
    zIndex: '10 !important',
    overflowX: 'hidden',
  },

  [`& .${classes.drawerHeader}`]: {
    'display': 'flex',
    'alignItems': 'center',
    'zIndex': 10,
    'position': 'sticky',
    'top': 0,
    'background': 'white',
    '& > svg': {
      marginRight: theme.spacing(2),
    },
    'padding': theme.spacing(3, 0, 2, 3),
  },

  [`& .${classes.drawerSection}`]: {
    padding: theme.spacing(3, 2, 0, 2),
    flexShrink: 0,
  },

  [`& .${classes.drawerSectionIndex}`]: {
    zIndex: -10,
  },

  [`& .${classes.closeBtn}`]: {
    flexGrow: 1,
    textAlign: 'right',
  },

  [`& .${classes.listIcon}`]: {
    color: theme.palette.common.black,
  },

  [`& .${classes.root}`]: {
    'color': theme.palette.text.secondary,
    '&:hover > .MuiTreeItem-content': {
      backgroundColor: theme.palette.action.hover,
    },
    '&:focus > .MuiTreeItem-content, & > .Mui-selected.MuiTreeItem-content': {
      backgroundColor: alpha(theme.palette.primary.main, 0.25),
      color: theme.palette.primary.main,
    },
    '&:focus > .MuiTreeItem-content .MuiTreeItem-label, &:hover > .MuiTreeItem-content .MuiTreeItem-label, & > .Mui-selected.MuiTreeItem-content .MuiTreeItem-label': {
      backgroundColor: 'transparent',
    },
  },

  [`& .${classes.content}`]: {
    'padding': 0,
    'color': theme.palette.text.secondary,
    'borderTopRightRadius': theme.spacing(2),
    'borderBottomRightRadius': theme.spacing(2),
    'fontWeight': theme.typography.fontWeightMedium,
    '.Mui-expanded > &': {
      fontWeight: theme.typography.fontWeightRegular,
    },
    '&>.MuiTreeItem-iconContainer': {
      width: 0,
      margin: 0,
    },
  },

  [`& .${classes.group}`]: {
    'marginLeft': 0,
    '& .MuiTreeItem-content': {
      paddingLeft: theme.spacing(2),
    },
  },

  [`& .${classes.label}`]: {
    fontWeight: 'inherit !important',
    color: 'inherit',
    borderTopRightRadius: theme.spacing(2),
    borderBottomRightRadius: theme.spacing(2),
    paddingLeft: 0,
  },

  [`& .${classes.labelRoot}`]: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0.5, 0, 0.5, 0.5),
    paddingLeft: 0,
  },

  [`& .${classes.labelIcon}`]: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(2),
  },

  [`& .${classes.labelText}`]: {
    fontWeight: 'inherit',
    flexGrow: 1,
    maxWidth: '150px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }
}));

export const DRAWER_WIDTH = 240;

function StyledTreeItem(props) {

  const {
    labelText, 
    labelIcon: LabelIcon, 
    labelInfo, 
    color, 
    bgColor, 
    ...other
  } = props;

  return (
    <TreeItem
      label={
        <Tooltip 
          title={labelText} 
          placement="top" 
          arrow
        >
          <div className={classes.labelRoot}>
            <LabelIcon color="inherit" className={classes.labelIcon} />
            <Typography variant="body2" className={classes.labelText}>
              {labelText}
            </Typography>
            <Typography variant="caption" color="inherit"></Typography>
          </div>
        </Tooltip>
      }
      style={{
        '--tree-view-color': color,
        '--tree-view-bg-color': bgColor,
      }}
      classes={{
        root: classes.root,
        content: classes.content,
        expanded: classes.expanded,
        selected: classes.selected,
        group: classes.group,
        label: classes.label,
      }}
      {...other}
    />
  );
}

StyledTreeItem.propTypes = {
  bgColor: PropTypes.string,
  color: PropTypes.string,
  labelIcon: PropTypes.elementType.isRequired,
  labelInfo: PropTypes.string,
  labelText: PropTypes.string.isRequired,
};

export default function ProjectsDrawer(props) {

  const {t} = useTranslation();

  const {instance}=useMsal();
  
  const {projectInfo, setProjectInfo} = useContext(ProjectInfoContext)
  const {userInfo} = useContext(UserInfoContext);
  const [snackbarState, setSnackbarState] = React.useState({
    open: false,
    severity: "success",
    message: ""
  });

  const handleProjectError=()=>{
    setProjectInfo(projectInfo);
    setSnackbarState({open: true, severity: "error", message: t('applicationErrorMessage.generic.projectError')});
  }

  const handleProjectChange=(el,value)=> {
   
    const project = userInfo.projects.find(proj => proj.id===parseInt(value));
    
    var jsonBody={project_id:project.id}

    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(jsonBody)
    };

    Auth.authorizedFetch("/api/setCurrentProject",requestOptions,instance)
    .then(res => res.json())
    .then((result) => {
      if (result.statusCode==200){
        setProjectInfo(project);
      }
      else{
        handleProjectError();
      }
    })
    .catch(()=>{
      handleProjectError();
    })
  };

  return (
    (<Root>
      <Drawer
        className={classes.drawer}
        variant="persistent"
        anchor="left"
        open={props.open}
        classes={{
          paper: classes.drawerPaper,
        }}
      >
        <div className={classes.drawerHeader}>
          <Typography variant='subtitle2' component='h2'>
            {t('drawer.projectsCaption')}
          </Typography>
        </div>
        <TreeView
          id='projects-list'
          className={classes.root}
          selected={`${projectInfo.id}`}
          onNodeSelect={handleProjectChange}
        >
          {userInfo.projects.map((project) => (
            <StyledTreeItem
              nodeId={`${project.id}`}
              key={`${project.id}`}
              labelText={project.id==0?t('drawer.itemAll'):project.contractNumber}
              labelIcon={FolderOpenIcon}
            />
          ))}
        </TreeView>
      </Drawer>
      <Snackbar 
        message={snackbarState.message}
        severity={snackbarState.severity}
        open={snackbarState.open}
        handleClose={()=>{setSnackbarState({...snackbarState, open: false, message: ""})}}
      />
    </Root>)
  );
}
ProjectsDrawer.propTypes={
  open: PropTypes.bool.isRequired,
}